"use client";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@scott/ui-d2c";
import type { CountryNavItem } from "@scott/ui-d2c/types";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";
import {
  COUNTRY_NAVIGATION_BAR_COOKIE_NAME,
  COUNTRY_NAVIGATION_BAR_SELECT_LOCATION_URL,
} from "@/utils/constants";
import {
  getUrlWithSelectedCountryAndLanguage,
  setCookie,
} from "@/utils/header.utils";
import {
  NavigationCheckIcon,
  NavigationChevronIcon,
  NavigationCloseIcon,
} from "./navigation-icons";

/** Same lifetime as the legacy header's country bar (setCookie takes minutes). */
const COOKIE_EXPIRY = 30;

interface NavigationCountryBarProps {
  componentData: CountryNavItem;
}

/** Suggests the visitor's own market above the Contentful navigation. */
export function NavigationCountryBar(
  props: Readonly<NavigationCountryBarProps>
) {
  const { buttonLabel, countryLanguageOptions, mainText, selectLocationLabel } =
    props.componentData;
  const defaultOption =
    countryLanguageOptions.find((option) => option.isSelected) ??
    countryLanguageOptions[0];
  const pathname = usePathname();
  const t = useTranslations("header.navigation");
  const [selectedLocationUrl, setSelectedLocationUrl] = useState(
    defaultOption?.url ?? COUNTRY_NAVIGATION_BAR_SELECT_LOCATION_URL
  );
  const [isClosed, setIsClosed] = useState(false);

  if (isClosed) {
    return null;
  }

  function close() {
    setIsClosed(true);
    setCookie(COUNTRY_NAVIGATION_BAR_COOKIE_NAME, "true", COOKIE_EXPIRY);
  }

  return (
    <div className="bg-black font-brand text-[12px] text-white leading-[17px]">
      <div className="mx-auto box-content flex max-w-[1420px] flex-wrap items-start justify-between gap-6 p-4 lg:flex-nowrap lg:items-center lg:gap-4 lg:px-10">
        <p className="min-w-0 flex-1 lg:truncate">{mainText}</p>
        <div className="order-last flex w-full items-center gap-4 lg:order-none lg:w-auto">
          <div className="min-w-0 flex-1 lg:w-[330px] lg:flex-none">
            <Select
              onValueChange={setSelectedLocationUrl}
              value={selectedLocationUrl}
            >
              <SelectTrigger
                className="group h-[42px] gap-0 rounded-[4px] border-0 bg-white px-4 py-[10px] font-brand text-[12px] text-black leading-[17px] hover:bg-white data-[state=open]:bg-white [&>svg]:hidden"
                icon={<NavigationCheckIcon />}
              >
                <span className="flex w-full items-center justify-between gap-2">
                  <span className="truncate opacity-80">
                    <SelectValue placeholder={selectLocationLabel} />
                  </span>
                  <span className="-rotate-90 flex shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90 motion-reduce:transition-none">
                    <NavigationChevronIcon />
                  </span>
                </span>
              </SelectTrigger>
              <SelectContent className="rounded-[4px] border-0 font-brand text-black">
                <SelectGroup>
                  {countryLanguageOptions.map(({ label, url }) => (
                    <SelectItem
                      className="text-[12px] leading-[17px]"
                      key={label}
                      value={url}
                    >
                      {label}
                    </SelectItem>
                  ))}
                  <SelectItem
                    className="text-[12px] leading-[17px]"
                    value={COUNTRY_NAVIGATION_BAR_SELECT_LOCATION_URL}
                  >
                    {selectLocationLabel}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <a
            className="flex h-[42px] shrink-0 items-center justify-center whitespace-nowrap rounded-[4px] bg-white px-4 text-black"
            href={getUrlWithSelectedCountryAndLanguage(
              pathname,
              selectedLocationUrl
            )}
          >
            {buttonLabel}
          </a>
        </div>
        <button
          aria-label={t("closeCountryBar")}
          className="flex shrink-0 cursor-pointer"
          onClick={close}
          type="button"
        >
          <NavigationCloseIcon />
        </button>
      </div>
    </div>
  );
}
